import {
  Cable,
  ChefHat,
  Network,
  Printer,
  ReceiptText,
  Tags,
} from 'lucide-react';
import { RestaurantPrinterDTO } from '../../../types/restaurant/RestaurantPrinterTypes';
import RestaurantTable, {
  RestaurantTableColumn,
} from '../shared/RestaurantTable';
import RestaurantTableActions from '../shared/RestaurantTableActions';

interface Props {
  printers: RestaurantPrinterDTO[];
  loading: boolean;
  onEdit: (printer: RestaurantPrinterDTO) => void;
  onDeactivate: (printer: RestaurantPrinterDTO) => void;
}

const typeLabels: Record<RestaurantPrinterDTO['printerType'], string> = {
  KITCHEN: 'Cocina',
  RECEIPT: 'Recibos',
  LABEL: 'Etiquetas',
};

const TypeIcon = ({ type }: { type: RestaurantPrinterDTO['printerType'] }) => {
  if (type === 'KITCHEN') return <ChefHat className="h-4 w-4" />;
  if (type === 'LABEL') return <Tags className="h-4 w-4" />;
  return <ReceiptText className="h-4 w-4" />;
};

const statusPill = (printer: RestaurantPrinterDTO) => {
  if (!printer.active) {
    return { label: 'Inactiva', cls: 'border-danger/20 bg-danger/10 text-danger' };
  }
  if (!printer.enabled) {
    return {
      label: 'Pausada',
      cls: 'border-warning/30 bg-warning/10 text-warning',
    };
  }
  return {
    label: 'Disponible',
    cls: 'border-success/20 bg-success/10 text-success',
  };
};

const connectionText = (printer: RestaurantPrinterDTO) =>
  printer.connectionType === 'NETWORK'
    ? `${printer.ipAddress ?? '—'}:${printer.port ?? '—'}`
    : 'Local / USB';

const ConnectionCell = ({ printer }: { printer: RestaurantPrinterDTO }) => (
  <span className="inline-flex items-center gap-1.5">
    {printer.connectionType === 'NETWORK' ? (
      <Network className="h-4 w-4" />
    ) : (
      <Cable className="h-4 w-4" />
    )}
    {connectionText(printer)}
  </span>
);

const RestaurantPrinterList = ({
  printers,
  loading,
  onEdit,
  onDeactivate,
}: Props) => {
  if (loading) {
    return (
      <div className="space-y-3 p-5">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-14 animate-pulse rounded-lg bg-gray-2 dark:bg-meta-4"
          />
        ))}
      </div>
    );
  }

  if (printers.length === 0) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center px-6 text-center">
        <span className="mb-4 rounded-full bg-primary/10 p-5 text-primary">
          <Printer className="h-9 w-9" />
        </span>
        <h3 className="text-lg font-semibold text-black dark:text-white">
          Aún no hay impresoras
        </h3>
        <p className="mt-2 max-w-md text-sm text-bodydark2 dark:text-bodydark">
          Registre el primer equipo para dirigir recibos, etiquetas y comandas de
          cocina en esta sucursal.
        </p>
      </div>
    );
  }

  const columns: RestaurantTableColumn<RestaurantPrinterDTO>[] = [
    {
      key: 'order',
      header: 'Orden',
      className: 'text-center font-semibold text-black dark:text-white',
      headClassName: 'text-center',
      cell: (p) => `#${p.displayOrder}`,
    },
    {
      key: 'name',
      header: 'Nombre',
      className: 'font-medium text-black dark:text-white',
      cell: (p) => (
        <div className="min-w-0">
          <p className="truncate">{p.name}</p>
          <p className="truncate text-xs font-normal text-bodydark2">
            {p.printerName}
          </p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Uso',
      className: 'text-sm text-black dark:text-white',
      cell: (p) => (
        <span className="inline-flex items-center gap-1.5">
          <TypeIcon type={p.printerType} />
          {typeLabels[p.printerType]}
        </span>
      ),
    },
    {
      key: 'connection',
      header: 'Conexión',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (p) => <ConnectionCell printer={p} />,
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (p) => {
        const pill = statusPill(p);
        return (
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${pill.cls}`}
          >
            {pill.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      headClassName: 'text-right',
      cell: (p) => (
        <RestaurantTableActions
          onEdit={() => onEdit(p)}
          onToggle={p.active ? () => onDeactivate(p) : undefined}
          active={p.active}
          toggleLabel={{ on: 'Desactivar', off: 'Desactivar' }}
        />
      ),
    },
  ];

  return (
    <>
      <RestaurantTable
        columns={columns}
        rows={printers}
        rowKey={(p) => p.id}
        rowClassName={(p) => (p.active ? '' : 'opacity-65')}
        minWidth="min-w-[900px]"
      />

      {/* Móvil: tarjetas */}
      <div className="grid grid-cols-1 gap-4 p-5 md:hidden">
        {printers.map((printer) => {
          const pill = statusPill(printer);
          return (
            <article
              key={printer.id}
              className={`rounded-xl border bg-white p-4 dark:bg-boxdark ${
                printer.active
                  ? 'border-stroke dark:border-strokedark'
                  : 'border-stroke opacity-65 dark:border-strokedark'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 gap-3">
                  <span className="rounded-lg bg-primary/10 p-2.5 text-primary">
                    <TypeIcon type={printer.printerType} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-black dark:text-white">
                      {printer.name}
                    </h3>
                    <p className="mt-0.5 truncate text-sm text-bodydark2 dark:text-bodydark">
                      {printer.printerName}
                    </p>
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${pill.cls}`}
                >
                  {pill.label}
                </span>
              </div>

              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-bodydark2 dark:text-bodydark">Uso</dt>
                  <dd className="font-medium text-black dark:text-white">
                    {typeLabels[printer.printerType]}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-bodydark2 dark:text-bodydark">Conexión</dt>
                  <dd className="font-medium text-black dark:text-white">
                    <ConnectionCell printer={printer} />
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-bodydark2 dark:text-bodydark">Orden</dt>
                  <dd className="font-medium text-black dark:text-white">
                    #{printer.displayOrder}
                  </dd>
                </div>
              </dl>

              <div className="mt-4 border-t border-stroke pt-3 dark:border-strokedark">
                <RestaurantTableActions
                  onEdit={() => onEdit(printer)}
                  onToggle={
                    printer.active ? () => onDeactivate(printer) : undefined
                  }
                  active={printer.active}
                  toggleLabel={{ on: 'Desactivar', off: 'Desactivar' }}
                />
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
};

export default RestaurantPrinterList;
